import React, {ChangeEvent, useState} from 'react';
import {Button, Space} from 'antd';
import {
    ArrowClockwiseIcon,
    ArrowCounterClockwiseIcon,
    ArrowLineUpIcon,
    ArrowsCounterClockwiseIcon,
    CameraIcon,
    DownloadSimpleIcon,
    FloppyDiskIcon,
    GearIcon,
    UploadSimpleIcon,
    XIcon
} from '@phosphor-icons/react';
import {useCurrentUser} from "../../../../contexts/UserContext";
import {useViewerStore} from '../../../../store/store';
import SingleModuleValidation from "../Modal/Modal-Content/SingleModuleValidation";
import FinaliseConfirm from "../Modal/Modal-Content/FinaliseConfirm";
import CertificationValidation from "../Modal/Modal-Content/CertificationValidation"
import LockedDesignValidation from "../Modal/Modal-Content/LockedDesignValidation"
import {IoIosLock} from "react-icons/io";
import {CheckSingleModuleRoofs} from "../Three-JS-Viewer/Controllers/ModuleControl";
import RoofPitchValidation from "../Modal/Modal-Content/RoofPitchValidation";
import TreePositionValidation from "../Modal/Modal-Content/TreePositionValidation";
import { ModuleMesh, RoofMesh } from '../../../../@types/types';
import * as THREE from 'three';
import {clickButtonWithRef, registerRef} from "../../../../helpers/storeHelpers";
import {checkIfAnyRoofHasModulesOverSetbackLines} from "../Three-JS-Viewer/Controllers/PathwayControl";
import ModuleOverSetbackValidation from "../Modal/Modal-Content/ModuleOverSetbackValidation";
import { AlertType } from '../../../../@types/enums';
import { EditorIconTooltip } from '../EditorIconTooltip';
import {
    useProductionCalculationReady,
    useSiteCalculationBlockedStatus,
} from '../../../../hooks/calculationStateHook';

const Actionbar = () => {

    const sceneViewer = useViewerStore(state => state.sceneViewer);
    const {user, hasCapability} = useCurrentUser();
    const isPushPullMode = useViewerStore((state) => state.isPushPullMode);
    const vendor = useViewerStore(state => state.selectedLossesVendor);
    const isBallastedRackingMeeting25percentRule = useViewerStore((state) => state.isBallastedRackingMeeting25percentRule);

    const selRoofFaceArr = useViewerStore((state) => state.selRoofFaceArr);
    const {designList, selectedDesignIndex} = useViewerStore(state => state);
    const loadingCompleted = useViewerStore((state) => state.loadingCompleted);
    const refreshingShade = useViewerStore((state) => state.refreshingShade);
    const shadeCalculatingIdx = useViewerStore((state) => state.shadeCalculatingIdx);
    const shadeCalculatingLength = useViewerStore((state) => state.shadeCalculatingLength);
    const productionValuesReady = useProductionCalculationReady();
    const calculationBlockedStatus = useSiteCalculationBlockedStatus({
        loadingCompleted,
        refreshingShade,
        productionValuesReady,
        shadeCalculatingIdx,
        shadeCalculatingLength,
    });
    const refreshShadeDisabled = calculationBlockedStatus != null;
    const refreshShadeTooltipTitle = calculationBlockedStatus ?? 'Refresh Shade';
    const [isOpen, setIsOpen] = useState(true);
    const stage = sessionStorage.getItem('currentStage');


    const exportGltf = () => {
        sceneViewer.downloadGLTF();
    }

    const importGltf = () => {
        clickButtonWithRef("gltfImport");
    }


    const pushPullHandler = () => {
        if (isPushPullMode) {
            sceneViewer.setPushPullMode(false);

            document.body.style.cursor = 'default';
        } else {
            sceneViewer.setPushPullMode(true);

            document.body.style.cursor = 'grab';
        }
        sceneViewer?.viewerAlert({
            show: !isPushPullMode,
            title: "Push Pull Tool Active",
            message: `Select a Tree or Obstruction and drag to extrude height of tree or obstruction. Press 'Esc' to exit.`,
            messageType: AlertType.info,
            isModal: false,
        });
    };

    const importGLBFile = (e: ChangeEvent<HTMLInputElement>) => {
        if(e.target.files && e.target.files[0]) sceneViewer.importGLBFile(e.target.files[0]);
    };

    const checkSelFaceArr = () => {
        const cloneFaceArr = [...selRoofFaceArr];
        cloneFaceArr.forEach(face => {
            face.moduleCount = face.children.filter((item: any) => {
                return item.module
            }).length;
        });
        useViewerStore.setState({selRoofFaceArr: cloneFaceArr});
    };

    const clickSnapShot = () => {
        const dataURL = sceneViewer.captureViewerScreenshot?.() ?? sceneViewer.renderer.domElement.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        a.download = 'oneDRAWScreenshot.png';
        a.click();
    };

    const handleUndo = () => {
        sceneViewer.undo();
        setTimeout(() => {
            checkSelFaceArr();
        }, 0);
    };

    const handleRedo = () => {
        sceneViewer.redo();
        setTimeout(() => {
            checkSelFaceArr();
        }, 0);
    };

    const handleRefresh = () => {
        sceneViewer.refreshShade();


    };

    const checkFinaliseConfirmation = () => {
        if (designList[0]?.Design_Certification_Status
            && designList[0]?.Design_Certification_Status !== "Not Certified"
            && designList[0]?.Design_Certification_Status !== null) {
            if (hasCapability("Pre_Installed_Certified")
                || hasCapability("Install_Change_Certified")
                || hasCapability("As_Built_Certified")
                || hasCapability("Certified_Design_Override")) {
                if (designList[0]?.Design_Certification_Status !== "Certified Design Override") {
                    sceneViewer.viewerAlert({
                        show: true,
                        title: <span style={{display: "flex"}}><IoIosLock
                            size={25}/> {designList[0]?.Design_Certification_Status}</span>,
                        message: <CertificationValidation confirm={finaliseSave}
                                                          certification_status={designList[0]?.Design_Certification_Status}/>,
                        messageType: AlertType.success,
                        isModal: true
                    })
                } else {
                    finaliseSave()
                }
            } else {
                sceneViewer.viewerAlert({
                    show: true,
                    title: <span style={{display: "flex"}}><IoIosLock
                        size={25}/> {designList[0]?.Design_Certification_Status}</span>,
                    message: <LockedDesignValidation certification_status={designList[0]?.Design_Certification_Status}/>,
                    messageType: AlertType.warning,
                    isModal: true
                })
            }
        } else {
            finaliseSave()
        }

    }

    const finaliseSave = () => {


        const doesDesignContainBallastedRacking = sceneViewer.moduleMeshArr.filter((module: ModuleMesh) => module.userData.isBallastedRackType).length > 0;
        if (doesDesignContainBallastedRacking && !isBallastedRackingMeeting25percentRule) {
            sceneViewer.viewerAlert({
                show: true,
                title: "25% Rule Violation for Ballasted Racking",
                message: "The design contains ballasted racking. Please ensure that the ballasted racking meets the 25% rule for the design to be finalized",
                messageType: AlertType.error,
                isModal: true
            });
            return

        }


        const isUserHaveSetbackOverrideCapability = useViewerStore.getState().isUserHaveCapabilityToAddPanelsPastSetbacks

        const checkIfModulesAreOverSetback = checkIfAnyRoofHasModulesOverSetbackLines()



        if (!isUserHaveSetbackOverrideCapability && checkIfModulesAreOverSetback) {
            sceneViewer.viewerAlert({
                show: true,
                title: "Error : Modules Over Setback Lines",
                message:<ModuleOverSetbackValidation/>,
                messageType: AlertType.error,
                isModal: true
            });
            return
        }



        const singleRoofs = CheckSingleModuleRoofs(sceneViewer.roofMeshArr);
        if (singleRoofs.length > 0) {

            sceneViewer.viewerAlert({
                show: true,
                title: "Single Module Array",
                message: <SingleModuleValidation roofs={singleRoofs}/>,
                messageType: AlertType.success,
                isModal: true
            })


        } else {

            const roofsHavingModulesOnSteepRoof = sceneViewer.roofMeshArr.filter((roof: RoofMesh) => {
                return roof.oriAng.tilt > 50 && roof?.moduleArr?.length > 0;
            })


            const isTreeCollidingWithRoof = sceneViewer.treeGroup.children.filter((tree: THREE.Object3D) => {
                return  tree.children[0].userData.isCollidingWithRoof
            })


            if (roofsHavingModulesOnSteepRoof.length > 0) {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Steep Roof Surface Design",
                    message: <RoofPitchValidation roofs={roofsHavingModulesOnSteepRoof}/>,
                    messageType: AlertType.success,
                    isModal: true
                })
            }
            else if (isTreeCollidingWithRoof.length > 0) {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Tree Position Warning",
                    message: <TreePositionValidation/>,
                    messageType: AlertType.warning,
                    isModal: true
                })

            }

            else {
                sceneViewer.viewerAlert({
                    show: true,
                    title: "Save Design",
                    message: <FinaliseConfirm/>,
                    messageType: AlertType.success,
                    modalClassName: 'modal__content--save-design',
                    isModal: true
                })
            }
        }

    }

    return (
        <div className={`toolbar toolbar--actionbar actions ${isOpen ? 'is-collapsed' : 'is-expanded'}`}>
            {
                user ?
                    <Space.Compact className="toolbar-btn-group">
                        <Button
                            className={!isOpen ? 'editor-toolbar-toggle editor-toolbar-toggle--close' : 'editor-toolbar-toggle'}
                            type='default'
                            size='large'
                            icon={
                                <EditorIconTooltip title={!isOpen ? 'Close Actionbar' : 'Open Actionbar'} zone="actionbar">
                                    <span className="editor-icon-tooltip__hit">
                                        {isOpen ? <GearIcon size={24} weight='regular'/> : <XIcon size={24} weight='bold'/>}
                                    </span>
                                </EditorIconTooltip>
                            }
                            onClick={() => setIsOpen(!isOpen)}
                        />

                        {!isOpen && (
                            <>
                            {hasCapability('Undo') && (
                                <Button
                                    ref={registerRef("undoBtn")}
                                    type='default'
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Undo" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <ArrowCounterClockwiseIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    onClick={handleUndo}
                                    property='StepUndo'
                                />
                            )}
                            {hasCapability('Redo') && (
                                <Button
                                    ref={registerRef("redoBtn")}
                                    type='default'
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Redo" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <ArrowClockwiseIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    onClick={handleRedo}
                                    property='StepRedo'
                                />
                            )}
                            {hasCapability('Download_GLB') && (
                                <Button
                                    type='default'
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Download GLB File" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <DownloadSimpleIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    onClick={exportGltf}
                                />
                            )}
                            {hasCapability('Download_GLB') && (
                            <Button
                                type='default'
                                size='large'
                                icon={
                                    <EditorIconTooltip title="Import GLB File" zone="actionbar">
                                        <span className="editor-icon-tooltip__hit">
                                            <UploadSimpleIcon size={22} weight='regular'/>
                                        </span>
                                    </EditorIconTooltip>
                                }
                                onClick={importGltf}
                            />
                            )}
                            {hasCapability('Refresh_Shade') && (
                                <EditorIconTooltip title={refreshShadeTooltipTitle} zone="actionbar">
                                    <span className="editor-icon-tooltip__hit">
                                        <Button
                                            type='default'
                                            size='large'
                                            disabled={refreshShadeDisabled}
                                            icon={<ArrowsCounterClockwiseIcon size={22} weight='regular'/>}
                                            onClick={handleRefresh}
                                        />
                                    </span>
                                </EditorIconTooltip>
                            )}
                            {hasCapability('Push_Pull Tool') && (
                                <Button
                                    ref={registerRef("pushPullToolBtn")}
                                    type={isPushPullMode ? 'primary' : 'default'}
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Push Pull Tool" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <ArrowLineUpIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    onClick={pushPullHandler}
                                />
                            )}
                            {hasCapability('Snapshot') && (
                                <Button
                                    type='default'
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Snapshot" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <CameraIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    id='snapShotBtn'
                                    onClick={clickSnapShot}
                                />
                            )}
                            {

                                ((hasCapability('Finalize') && stage && Number(stage) < 4) ||
                                    (hasCapability('Finalize') && stage && Number(stage) >= 4 && hasCapability('Stage_4_Post_Contract_Finalize')))
                                && <Button
                                    type='primary'
                                    size='large'
                                    icon={
                                        <EditorIconTooltip title="Save Design" zone="actionbar">
                                            <span className="editor-icon-tooltip__hit">
                                                <FloppyDiskIcon size={22} weight='regular'/>
                                            </span>
                                        </EditorIconTooltip>
                                    }
                                    onClick={checkFinaliseConfirmation}
                                >Finalize</Button>}
                            <input ref={registerRef("gltfImport")} type='file' accept='.zip' onChange={importGLBFile}  hidden/>
                            </>
                        )}
                    </Space.Compact>
                    : ""
            }
        </div>
    );
}

export default Actionbar;
